<template>
  <SideBar />
  <div class="p-4 sm:ml-64 min-h-screen dark:bg-gray-900 text-white">
    <div class="container mx-auto p-4">
      <h1 class="text-3xl font-bold mb-6">Estadísticas de Presupuestos</h1>

      <!-- 🔹 Formulario de búsqueda -->
      <div class="bg-gray-800 p-4 rounded-lg shadow-lg mb-6">
        <!-- Botones de filtros predeterminados -->
        <div class="mb-4">
          <label class="block text-gray-400 mb-2">Filtros rápidos:</label>
          <div class="flex flex-wrap gap-2">
            <button
              @click="aplicarFiltroRapido('ultimoMes')"
              type="button"
              :class="[
                'px-3 py-2 rounded-lg text-sm font-medium transition',
                filtroActivo === 'ultimoMes'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              ]"
            >
              Último mes
            </button>
            <button
              @click="aplicarFiltroRapido('ultimoTrimestre')"
              type="button"
              :class="[
                'px-3 py-2 rounded-lg text-sm font-medium transition',
                filtroActivo === 'ultimoTrimestre'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              ]"
            >
              Último trimestre
            </button>
            <button
              @click="aplicarFiltroRapido('ultimoAnio')"
              type="button"
              :class="[
                'px-3 py-2 rounded-lg text-sm font-medium transition',
                filtroActivo === 'ultimoAnio'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              ]"
            >
              Último año
            </button>
            <button
              @click="aplicarFiltroRapido('desdeInicioAnio')"
              type="button"
              :class="[
                'px-3 py-2 rounded-lg text-sm font-medium transition',
                filtroActivo === 'desdeInicioAnio'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              ]"
            >
              Desde inicio del año
            </button>
            <button
              @click="aplicarFiltroRapido('personalizado')"
              type="button"
              :class="[
                'px-3 py-2 rounded-lg text-sm font-medium transition',
                filtroActivo === 'personalizado'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              ]"
            >
              Personalizado
            </button>
          </div>
        </div>

        <form @submit.prevent="cargarPresupuestos">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div class="w-full">
              <label class="block text-gray-400 mb-2" for="fechaInicio"
                >Fecha Inicio</label
              >
              <input
                v-model="fechaInicio"
                type="date"
                id="fechaInicio"
                :disabled="filtroActivo !== 'personalizado'"
                :class="[
                  'w-full p-2 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500',
                  filtroActivo !== 'personalizado'
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-700'
                ]"
              />
            </div>

            <div class="w-full">
              <label class="block text-gray-400 mb-2" for="fechaFin"
                >Fecha Fin</label
              >
              <input
                v-model="fechaFin"
                type="date"
                id="fechaFin"
                :disabled="filtroActivo !== 'personalizado'"
                :class="[
                  'w-full p-2 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500',
                  filtroActivo !== 'personalizado'
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-700'
                ]"
              />
            </div>

            <div class="w-full">
              <label class="block text-gray-400 mb-2" for="fechaFin"
                >Filtrar</label
              >
              <button
                type="submit"
                class="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-lg transition md:mt-0"
              >
                Aplicar filtros
              </button>
            </div>
          </div>
        </form>
      </div>

      <!-- 🔹 Resumen -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div class="bg-gray-800 p-4 rounded-2xl shadow-lg">
          <h2 class="text-lg mb-2">💰 Total de Presupuestos</h2>
          <p class="text-3xl font-semibold">
            ${{ totalPresupuestos.toLocaleString("es-AR", { maximumFractionDigits: 0, minimumFractionDigits: 0 }) }}
          </p>
        </div>

        <div class="bg-gray-800 p-4 rounded-2xl shadow-lg">
          <h2 class="text-lg mb-2">📈 Valor promedio de presupuesto</h2>
          <p class="text-3xl font-semibold">
            ${{ promedioPresupuestos.toLocaleString("es-AR", { maximumFractionDigits: 0, minimumFractionDigits: 0 }) }}
          </p>
        </div>

        <div class="bg-gray-800 p-4 rounded-2xl shadow-lg">
          <h2 class="text-lg mb-2">🧾 Cantidad de Presupuestos</h2>
          <p class="text-3xl font-semibold">{{ cantidadPresupuestos }}</p>
        </div>
      </div>

      <!-- 🔹 Selector de agrupación -->
      <div class="mt-10 flex items-center gap-3">
        <label for="agrupacion" class="text-sm text-gray-400"
          >Agrupar por:</label
        >
        <select
          id="agrupacion"
          v-model="modoAgrupacion"
          class="bg-gray-800 text-white border border-gray-700 rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-600"
        >
          <option value="dia">Día</option>
          <option value="semana">Semana</option>
          <option value="mes">Mes</option>
          <option value="anio">Año</option>
        </select>
      </div>

      <!-- 🔹 Gráfico de cantidad -->
      <div class="mt-10 bg-gray-800 p-6 rounded-2xl shadow-lg">
        <h2 class="text-lg mb-4">
          Cantidad de Presupuestos por {{ textoAgrupacion }}
        </h2>
        <apexchart
          width="100%"
          height="300"
          type="line"
          :options="chartOptionsCantidad"
          :series="seriesCantidad"
        />
      </div>

      <!-- 🔹 Gráfico de totales -->
      <div class="mt-10 bg-gray-800 p-6 rounded-2xl shadow-lg">
        <h2 class="text-lg mb-4">
          Total de Presupuestos por {{ textoAgrupacion }}
        </h2>
        <apexchart
          width="100%"
          height="300"
          type="bar"
          :options="chartOptionsTotales"
          :series="seriesTotales"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import SideBar from "../components/NavBar.vue"; // ✅ mover aquí la importación
import { ref, computed, onMounted } from "vue";
import VueApexCharts from "vue3-apexcharts";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import PresupuestosProvider from "@/helpers/providers/PresupuestosProvider";

dayjs.extend(isoWeek);

// Registrar el componente de ApexCharts
const apexchart = VueApexCharts;

// Estado reactivo
const presupuestos = ref([]);
const fechaInicio = ref("");
const fechaFin = ref("");
const filtroActivo = ref("ultimoMes");

// Funciones para filtros rápidos
const aplicarFiltroRapido = (tipo) => {
  filtroActivo.value = tipo;
  const hoy = dayjs();
  
  switch (tipo) {
    case 'ultimoMes':
      fechaInicio.value = hoy.subtract(1, 'month').format('YYYY-MM-DD');
      fechaFin.value = hoy.format('YYYY-MM-DD');
      break;
    case 'ultimoTrimestre':
      fechaInicio.value = hoy.subtract(3, 'month').format('YYYY-MM-DD');
      fechaFin.value = hoy.format('YYYY-MM-DD');
      break;
    case 'ultimoAnio':
      fechaInicio.value = hoy.subtract(1, 'year').format('YYYY-MM-DD');
      fechaFin.value = hoy.format('YYYY-MM-DD');
      break;
    case 'desdeInicioAnio':
      fechaInicio.value = hoy.startOf('year').format('YYYY-MM-DD');
      fechaFin.value = hoy.format('YYYY-MM-DD');
      break;
    case 'personalizado':
      // No cambiar fechas, permitir al usuario configurar manualmente
      break;
  }
  
  // Auto-cargar los presupuestos excepto para personalizado
  if (tipo !== 'personalizado') {
    cargarPresupuestos();
  }
};

// Inicializar filtro por defecto
const inicializarFiltroDefecto = () => {
  aplicarFiltroRapido('ultimoMes');
};

// Cargar presupuestos
const cargarPresupuestos = async () => {
  try {
    const data = await PresupuestosProvider.getPresupuestos_stats(
      fechaInicio.value,
      fechaFin.value
    );
    presupuestos.value = Array.isArray(data) ? data : data.data || [];
  } catch (error) {
    console.error("Error al cargar presupuestos:", error);
  }
};

onMounted(inicializarFiltroDefecto);

// Agrupación
const modoAgrupacion = ref("dia");
const totalPresupuestos = computed(() =>
  presupuestos.value.reduce((acc, p) => acc + parseFloat(p.TOTAL), 0)
);
const promedioPresupuestos = computed(() =>
  presupuestos.value.length
    ? totalPresupuestos.value / presupuestos.value.length
    : 0
);
const cantidadPresupuestos = computed(() => presupuestos.value.length);

// Agrupamiento por periodo
const presupuestosAgrupados = computed(() => {
  const map = {};

  presupuestos.value.forEach((p) => {
    const fecha = dayjs(p.DATE);
    let clave, fechaOrden;

    switch (modoAgrupacion.value) {
      case "semana":
        // usamos lunes de esa semana como referencia
        clave = `Semana ${fecha.isoWeek()} - ${fecha.year()}`;
        fechaOrden = fecha.startOf("week").toDate();
        break;
      case "mes":
        clave = fecha.format("MMM-YY");
        fechaOrden = fecha.startOf("month").toDate();
        break;
      case "anio":
        clave = fecha.format("YYYY");
        fechaOrden = fecha.startOf("year").toDate();
        break;
      default:
        clave = fecha.format("DD-MM-YY");
        fechaOrden = fecha.startOf("day").toDate();
    }

    if (!map[clave]) map[clave] = { montos: [], fechaOrden };
    map[clave].montos.push(parseFloat(p.TOTAL));
  });

  return Object.entries(map)
    .sort((a, b) => a[1].fechaOrden - b[1].fechaOrden) // ✅ orden cronológico real
    .map(([clave, { montos, fechaOrden }]) => ({
      periodo: clave,
      cantidad: montos.length,
      total: montos.reduce((a, b) => a + b, 0),
      fechaOrden,
    }));
});

// Series y opciones del gráfico
const seriesCantidad = computed(() => [
  {
    name: "Cantidad",
    data: presupuestosAgrupados.value.map((p) => p.cantidad),
  },
]);
const seriesTotales = computed(() => [
  {
    name: "Total (ARS)",
    data: presupuestosAgrupados.value.map((p) => p.total),
  },
]);

const chartOptionsBase = {
  chart: { toolbar: { show: false }, foreColor: "#ccc" },
  grid: { borderColor: "#444" },
  tooltip: { theme: "dark", style: { fontSize: "14px", color: "#fff" } },
};
const chartOptionsCantidad = computed(() => ({
  ...chartOptionsBase,
  xaxis: { categories: presupuestosAgrupados.value.map((p) => p.periodo) },
  stroke: { curve: "smooth" },
  colors: ["#00E396"],
}));
const chartOptionsTotales = computed(() => ({
  ...chartOptionsBase,
  xaxis: { categories: presupuestosAgrupados.value.map((p) => p.periodo) },
  colors: ["#FEB019"],
  dataLabels: { enabled: false },
}));

// Texto agrupación
const textoAgrupacion = computed(() => {
  switch (modoAgrupacion.value) {
    case "semana":
      return "Semana";
    case "mes":
      return "Mes";
    case "anio":
      return "Año";
    default:
      return "Día";
  }
});
</script>

<style scoped>
body {
  background-color: #121212;
}
</style>
